/**
 * Iter 165 · Widget de salud del cache PLACSP.
 *
 * Solo visible para admin. Muestra el total de licitaciones cacheadas,
 * la última sincronización y los jobs en curso. Botón "Refrescar ahora".
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Database, RefreshCw, Loader2, CheckCircle2, Clock, AlertOctagon } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

function relTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "hace " + Math.round(diff) + "s";
    if (diff < 3600) return "hace " + Math.round(diff / 60) + " min";
    if (diff < 86400) return "hace " + Math.round(diff / 3600) + " h";
    return "hace " + Math.round(diff / 86400) + " d";
  } catch { return "—"; }
}

export default function PlacspCacheHealthWidget() {
  const { user } = useAuth() || {};
  const role = (user?.role || "").toLowerCase();
  const visible = role === "admin" || role === "gestor";
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [s, j] = await Promise.all([api.get("/placsp/stats"), api.get("/placsp/jobs")]);
      setStats(s.data);
      setJobs(j.data || []);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!visible) return;
    cargar();
    const t = setInterval(cargar, 60_000);
    return () => clearInterval(t);
  }, [visible, cargar]);

  if (!visible || !stats) return null;

  const running = jobs.filter((j) => j.status === "running" || j.status === "queued");
  const lastSync = jobs.find((j) => j.status === "done" || j.status === "completed")?.created_at;
  const total = stats.total || 0;
  const byDs = stats.by_dataset || {};

  const refrescar = async () => {
    setBusy(true);
    try {
      const year = new Date().getFullYear();
      await Promise.all([
        api.post("/placsp/sync", { dataset: "perfiles", year }),
        api.post("/placsp/sync", { dataset: "agregadas", year }),
        api.post("/placsp/sync", { dataset: "menores", year }),
      ]);
      toast.success("Sincronización iniciada · vuelve en 2-5 min");
      setTimeout(cargar, 1500);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  // Iter 166 · Botón rojo solo admin: dispara los 6 jobs (3 datasets × año actual + anterior)
  // como tareas asyncio en el servidor (no BackgroundTasks que se cancelan al cerrar la
  // request). Útil cuando el bootstrap del arranque falló silenciosamente en producción.
  const bootstrapForzado = async () => {
    if (!window.confirm("Forzar bootstrap completo: descargará los 6 ZIPs anuales del feed PLACSP. ¿Continuar?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/placsp/bootstrap-force");
      toast.success(`Bootstrap forzado · ${data.queued?.length || 0} jobs encolados (2-10 min)`);
      setTimeout(cargar, 1500);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const empty = total === 0;
  const health = empty ? "critical" : running.length > 0 ? "syncing" : "ok";
  const cfg = {
    ok: { color: "#15803D", bg: "#15803D14", label: "Cache saludable" },
    syncing: { color: "#1E5BCC", bg: "#1E5BCC14", label: "Sincronizando…" },
    critical: { color: "#DC2626", bg: "#DC262614", label: "Cache vacío" },
  }[health];

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] p-4"
      style={{ boxShadow: empty ? "inset 0 0 0 1px #DC262633" : undefined }}
      data-testid="placsp-cache-health"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white shrink-0"
             style={{ background: `linear-gradient(135deg, ${cfg.color}, ${cfg.color}CC)` }}>
          <Database className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-sm">Cache PLACSP</h3>
            <Badge className="text-[9px] uppercase tracking-wider"
                   style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}55` }}>
              {health === "syncing" ? <Loader2 className="h-3 w-3 mr-1 inline animate-spin" /> :
               health === "critical" ? <AlertOctagon className="h-3 w-3 mr-1 inline" /> :
               <CheckCircle2 className="h-3 w-3 mr-1 inline" />}
              {cfg.label}
            </Badge>
          </div>
          <div className="text-2xl font-extrabold tabular-nums mt-1" style={{ color: cfg.color }} data-testid="placsp-cache-total">
            {total.toLocaleString("es-ES")}
            <span className="text-xs font-bold text-[var(--legatum-text-muted)] ml-1">licitaciones</span>
          </div>
          <div className="text-[10px] text-[var(--legatum-text-muted)] flex items-center gap-2 flex-wrap mt-1">
            <Clock className="h-3 w-3" />
            <span>Última sync: <b>{relTime(lastSync)}</b></span>
            {Object.keys(byDs).length > 0 && (
              <span>· {Object.entries(byDs).map(([k, v]) => `${k}:${v}`).join(" · ")}</span>
            )}
          </div>
          {running.length > 0 && (
            <div className="text-[10px] text-[#1E5BCC] mt-1" data-testid="placsp-cache-running">
              <Loader2 className="h-3 w-3 inline mr-1 animate-spin" />
              {running.length} {running.length === 1 ? "job en curso" : "jobs en curso"} ({running.map((j) => j.dataset).join(", ")})
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 items-end shrink-0">
          <Button size="sm" variant="outline" disabled={busy || running.length > 0}
                  onClick={refrescar} data-testid="placsp-cache-refresh"
                  className="text-[10px]">
            <RefreshCw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refrescar
          </Button>
          {role === "admin" && (empty || jobs.some((j) => j.status === "error")) && (
            <Button size="sm" variant="destructive" disabled={busy}
                    onClick={bootstrapForzado} data-testid="placsp-cache-bootstrap-force"
                    className="text-[10px]" title="Forzar descarga completa (año actual + anterior)">
              <AlertOctagon className="h-3 w-3 mr-1" /> Forzar bootstrap
            </Button>
          )}
          <Link to="/licitaciones" className="text-[10px] text-[var(--legatum-accent)] hover:underline">
            Ver Buscador →
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
